/**
 * JobTypeGrid Component
 * Premium grid of job types with Lucide icons and glassmorphism
 */

import React, { useState } from "react";
import { JobTypeCard } from "../jobs/JobTypeCard";
import { Card, CardHeader, Modal, Badge, Button } from "../common";
import { JobType } from "../../types";
import {
  getStandardJobTypes,
  getAdvancedJobTypes,
  JobTypeConfig,
} from "../../services/jobTypes";
import {
  Bot,
  BarChart3,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  Rocket,
  Users,
  Car,
  ParkingCircle,
  ArrowLeftRight,
  CircleDot,
  Utensils,
  BedDouble,
  Building2,
  PawPrint,
  HardHat,
  Box,
  MonitorPlay,
  Radar,
  Camera,
  Thermometer,
  Stethoscope,
  Microscope,
  Ruler,
  Satellite,
} from "lucide-react";

// Job type to icon mapping
const JOB_TYPE_ICONS: Record<string, React.ReactNode> = {
  people_counting: <Users className="w-6 h-6" />,
  number_plate_detection: <Car className="w-6 h-6" />,
  parking_analysis: <ParkingCircle className="w-6 h-6" />,
  in_out_counting: <ArrowLeftRight className="w-6 h-6" />,
  pothole_detection: <CircleDot className="w-6 h-6" />,
  food_waste_estimation: <Utensils className="w-6 h-6" />,
  room_readiness: <BedDouble className="w-6 h-6" />,
  lobby_crowd_detection: <Building2 className="w-6 h-6" />,
  wildlife_detection: <PawPrint className="w-6 h-6" />,
  ppe_detection: <HardHat className="w-6 h-6" />,
  damage_detection_3d: <Box className="w-6 h-6" />,
  multi_camera: <MonitorPlay className="w-6 h-6" />,
  lidar_analysis: <Satellite className="w-6 h-6" />,
  radar_analysis: <Radar className="w-6 h-6" />,
  rgb_camera: <Camera className="w-6 h-6" />,
  thermal_camera: <Thermometer className="w-6 h-6" />,
  medical_imaging: <Stethoscope className="w-6 h-6" />,
  borescopic_camera: <Microscope className="w-6 h-6" />,
  product_design: <Ruler className="w-6 h-6" />,
};

interface JobTypeGridProps {
  onSelectJobType: (jobType: JobType) => void;
}

export const JobTypeGrid: React.FC<JobTypeGridProps> = ({
  onSelectJobType,
}) => {
  const [standardExpanded, setStandardExpanded] = useState(true);
  const [advancedExpanded, setAdvancedExpanded] = useState(true);
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);
  const [selectedComingSoonJob, setSelectedComingSoonJob] =
    useState<JobTypeConfig | null>(null);

  const standardJobTypes = getStandardJobTypes();
  const advancedJobTypes = getAdvancedJobTypes();

  const handleAdvancedCardClick = (config: JobTypeConfig) => {
    if (config.externalLink) {
      window.open(config.externalLink, "_blank", "noopener,noreferrer");
      return;
    }
    if (config.status === "coming_soon") {
      setSelectedComingSoonJob(config);
      setComingSoonModalOpen(true);
      return;
    }
    if (config.status === "active") {
      onSelectJobType(config.type);
    }
  };

  const getStatusBadge = (config: JobTypeConfig) => {
    if (config.externalLink) {
      return (
        <Badge variant="success" size="sm">
          External Tool
        </Badge>
      );
    }
    if (config.status === "coming_soon") {
      return (
        <Badge variant="warning" size="sm">
          Coming Soon
        </Badge>
      );
    }
    if (config.status === "beta") {
      return (
        <Badge variant="info" size="sm">
          Beta
        </Badge>
      );
    }
    if (config.isConfigBased) {
      return (
        <Badge variant="info" size="sm">
          Config-Based
        </Badge>
      );
    }
    return (
      <Badge variant="primary" size="sm">
        Active
      </Badge>
    );
  };

  const getIcon = (config: JobTypeConfig) => {
    return JOB_TYPE_ICONS[config.type] || <BarChart3 className="w-6 h-6" />;
  };

  return (
    <div className="space-y-6">
      {/* Advanced Analysis Section */}
      <Card variant="glass" padding="lg">
        <CardHeader
          title="Advanced Analysis"
          subtitle="External services and advanced analytics"
          icon={<Bot className="w-5 h-5" />}
          action={
            <button
              onClick={() => setAdvancedExpanded(!advancedExpanded)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
            >
              {advancedExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          }
        />

        {advancedExpanded && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {advancedJobTypes.map((config) => (
              <div
                key={config.type}
                className={`cursor-pointer group relative overflow-hidden rounded-xl ${
                  config.status === "coming_soon" ? "opacity-75" : ""
                }`}
                onClick={() => handleAdvancedCardClick(config)}
              >
                <div className="flex flex-col items-center p-6 bg-slate-800/30 hover:bg-slate-700/50 border border-slate-700/50 hover:border-primary-500/30 rounded-xl transition-all h-full">
                  {/* Icon */}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center text-primary-400 group-hover:scale-110 transition-transform duration-200 mb-4">
                    {getIcon(config)}
                  </div>

                  {/* Label */}
                  <h3 className="text-base font-semibold text-white group-hover:text-primary-400 transition-colors text-center">
                    {config.label}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-slate-400 text-center mt-2 line-clamp-2">
                    {config.description}
                  </p>

                  {/* Badge */}
                  <div className="mt-4">{getStatusBadge(config)}</div>

                  {/* External link indicator */}
                  {config.externalLink && (
                    <div className="absolute top-3 right-3 text-slate-400">
                      <ExternalLink className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Standard Analysis Section */}
      <Card variant="glass" padding="lg">
        <CardHeader
          title="Standard Analysis"
          subtitle="Computer vision analytics"
          icon={<BarChart3 className="w-5 h-5" />}
          action={
            <button
              onClick={() => setStandardExpanded(!standardExpanded)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all"
            >
              {standardExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
          }
        />

        {standardExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {standardJobTypes.map((config) => (
              <JobTypeCard
                key={config.type}
                jobType={config.type}
                onClick={onSelectJobType}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Coming Soon Modal */}
      <Modal
        isOpen={comingSoonModalOpen}
        onClose={() => setComingSoonModalOpen(false)}
        title="Coming Soon"
        icon={<Rocket className="w-6 h-6" />}
        size="md"
      >
        <div className="flex flex-col items-center py-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center text-primary-400 mb-4">
            {selectedComingSoonJob && getIcon(selectedComingSoonJob)}
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {selectedComingSoonJob?.label}
          </h3>
          <p className="text-slate-400 text-center mb-6 max-w-sm">
            {selectedComingSoonJob?.description}
          </p>
          <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl p-4 mb-6 max-w-sm">
            <p className="text-primary-300 text-sm text-center">
              This advanced analysis feature is currently under development and
              will be available soon!
            </p>
          </div>
          <Button variant="gradient" onClick={() => setComingSoonModalOpen(false)}>
            Got it!
          </Button>
        </div>
      </Modal>
    </div>
  );
};
